import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer, IconButton, Tooltip, Alert, CircularProgress, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Stack,
  FormControlLabel, Checkbox, List, ListItem, ListItemButton, ListItemIcon,
  ListItemText, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { htmlDocsAPI, techniciansAPI } from '../../services/api';
import HtmlDocViewer from './HtmlDocViewer';

const fmtSize = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('es-ES') : '—');

/**
 * Documentos HTML generados fuera de la aplicacion (checklists, presentaciones,
 * fichas de maquina...). Aqui se suben, se prueban y se entregan a los tecnicos,
 * que los ven desde su pestana HTML.
 */
export default function TaskHtmlDocs() {
  const [docs, setDocs] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [upload, setUpload] = useState(null);      // { file, title, description }
  const [edit, setEdit] = useState(null);          // doc en edicion
  const [assign, setAssign] = useState(null);      // { doc, ids:Set, all:bool }
  const [viewer, setViewer] = useState(null);      // doc que se esta viendo
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await htmlDocsAPI.list();
      setDocs(res.data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'No se han podido cargar los documentos.');
      setDocs([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    techniciansAPI.list().then((r) => setTechnicians(r.data)).catch(() => {});
  }, []);

  const openUpload = () => setUpload({ file: null, title: '', description: '' });

  const pickFile = (file) => {
    if (!file) return;
    setUpload((u) => ({
      ...u,
      file,
      // La denominacion se propone a partir del nombre del fichero, y se edita
      title: u.title || file.name.replace(/\.html?$/i, ''),
    }));
  };

  const saveUpload = async () => {
    if (!upload.file || !upload.title.trim()) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', upload.file);
      form.append('title', upload.title.trim());
      form.append('description', upload.description || '');
      await htmlDocsAPI.create(form);
      setUpload(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se ha podido subir el documento.');
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await htmlDocsAPI.update(edit.id, {
        title: edit.title, description: edit.description,
      });
      if (edit.newFile) {
        const form = new FormData();
        form.append('file', edit.newFile);
        await htmlDocsAPI.replaceFile(edit.id, form);
      }
      setEdit(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se ha podido guardar.');
    } finally {
      setBusy(false);
    }
  };

  const toggleHidden = async (doc) => {
    try {
      await htmlDocsAPI.update(doc.id, { is_hidden: !doc.is_hidden });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se ha podido cambiar la visibilidad.');
    }
  };

  const openAssign = (doc) => setAssign({
    doc, ids: new Set(doc.technician_ids || []), all: !!doc.assigned_all,
  });

  const saveAssign = async () => {
    setBusy(true);
    try {
      await htmlDocsAPI.assign(assign.doc.id, {
        technician_ids: Array.from(assign.ids),
        assigned_all: assign.all,
      });
      setAssign(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se ha podido asignar.');
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await htmlDocsAPI.delete(confirmDelete.id);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se ha podido quitar el documento.');
    } finally {
      setBusy(false);
    }
  };

  const assignedLabel = (doc) => {
    if (doc.assigned_all) return <Chip size="small" color="success" label="Todos los tecnicos" />;
    const names = (doc.technicians || []).map((t) => t.name).filter(Boolean);
    if (!names.length) {
      return <Typography variant="body2" color="error">Sin asignar</Typography>;
    }
    if (names.length <= 2) {
      return names.map((n) => <Chip key={n} size="small" label={n} sx={{ mr: 0.5 }} />);
    }
    return (
      <Tooltip title={names.join(', ')}>
        <Chip size="small" label={`${names.length} tecnicos`} />
      </Tooltip>
    );
  };

  if (docs === null) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 2 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Documentos HTML generados fuera de la aplicacion. Se asignan a los tecnicos,
          que los abren desde su pestana HTML. Ocultar lo retira de su vista sin
          perder las asignaciones.
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openUpload}>
          Nuevo HTML
        </Button>
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#c62828' }}>
              {['Denominacion', 'Descripcion', 'Fichero', 'Asignado a', 'Estado', 'Alta', '']
                .map((h, i) => (
                  <TableCell key={h || i} sx={{ fontWeight: 'bold', color: '#fff' }}>{h}</TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {docs.map((doc) => (
              <TableRow key={doc.id} hover
                sx={{ ...(doc.is_hidden ? { backgroundColor: '#fafafa', opacity: 0.7 } : {}) }}>
                <TableCell sx={{ fontWeight: 500 }}>{doc.title}</TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary"
                    sx={{ maxWidth: 320 }}>
                    {doc.description || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{doc.original_filename || '—'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fmtSize(doc.size_bytes)}
                  </Typography>
                </TableCell>
                <TableCell>{assignedLabel(doc)}</TableCell>
                <TableCell>
                  <Chip size="small" label={doc.is_hidden ? 'Oculto' : 'Visible'}
                    color={doc.is_hidden ? 'default' : 'primary'} />
                </TableCell>
                <TableCell>{fmtDate(doc.created_at)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="Ver y usar">
                    <IconButton size="small" onClick={() => setViewer(doc)}>
                      <OpenInFullIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Asignar a tecnicos">
                    <IconButton size="small" onClick={() => openAssign(doc)}>
                      <GroupAddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={doc.is_hidden ? 'Mostrar' : 'Ocultar'}>
                    <IconButton size="small" onClick={() => toggleHidden(doc)}>
                      {doc.is_hidden
                        ? <VisibilityOffIcon fontSize="small" />
                        : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => setEdit({
                      ...doc, description: doc.description || '', newFile: null,
                    })}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Quitar">
                    <IconButton size="small" color="error"
                      onClick={() => setConfirmDelete(doc)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {docs.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    Todavia no hay documentos HTML. Sube el primero con "Nuevo HTML".
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Alta: fichero .html generado fuera */}
      <Dialog open={!!upload} onClose={() => setUpload(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuevo documento HTML</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Button variant="outlined" component="label">
              {upload?.file ? upload.file.name : 'Seleccionar fichero .html'}
              <input hidden type="file" accept=".html,.htm,text/html"
                onChange={(e) => pickFile(e.target.files?.[0])} />
            </Button>
            <TextField label="Denominacion" fullWidth required
              value={upload?.title || ''}
              onChange={(e) => setUpload({ ...upload, title: e.target.value })} />
            <TextField label="Descripcion" fullWidth multiline rows={3}
              helperText="Lo que vera el tecnico en el listado para saber para que sirve"
              value={upload?.description || ''}
              onChange={(e) => setUpload({ ...upload, description: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpload(null)}>Cancelar</Button>
          <Button variant="contained" onClick={saveUpload}
            disabled={busy || !upload?.file || !upload?.title?.trim()}>
            Subir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edicion: denominacion, descripcion y sustitucion del fichero */}
      <Dialog open={!!edit} onClose={() => setEdit(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar documento</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Denominacion" fullWidth required
              value={edit?.title || ''}
              onChange={(e) => setEdit({ ...edit, title: e.target.value })} />
            <TextField label="Descripcion" fullWidth multiline rows={3}
              value={edit?.description || ''}
              onChange={(e) => setEdit({ ...edit, description: e.target.value })} />
            <Button variant="outlined" component="label">
              {edit?.newFile ? edit.newFile.name : 'Sustituir el fichero (opcional)'}
              <input hidden type="file" accept=".html,.htm,text/html"
                onChange={(e) => setEdit({ ...edit, newFile: e.target.files?.[0] || null })} />
            </Button>
            <Typography variant="caption" color="text.secondary">
              Fichero actual: {edit?.original_filename || '—'}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEdit(null)}>Cancelar</Button>
          <Button variant="contained" onClick={saveEdit}
            disabled={busy || !edit?.title?.trim()}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Asignacion a tecnicos concretos o a todos */}
      <Dialog open={!!assign} onClose={() => setAssign(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Asignar «{assign?.doc?.title}»</DialogTitle>
        <DialogContent dividers>
          <FormControlLabel
            control={<Checkbox checked={!!assign?.all}
              onChange={(e) => setAssign({ ...assign, all: e.target.checked })} />}
            label="Todos los tecnicos"
          />
          <Divider sx={{ my: 1 }} />
          {assign?.all && (
            <Alert severity="info" sx={{ mb: 1 }}>
              Con «Todos los tecnicos» marcado, el documento llega a cualquiera con
              acceso a la app, incluidos los que se den de alta mas adelante.
            </Alert>
          )}
          <List dense disablePadding>
            {technicians.map((t) => (
              <ListItem key={t.id} disablePadding>
                <ListItemButton dense disabled={!!assign?.all}
                  onClick={() => {
                    const ids = new Set(assign.ids);
                    if (ids.has(t.id)) ids.delete(t.id); else ids.add(t.id);
                    setAssign({ ...assign, ids });
                  }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox edge="start" disableRipple tabIndex={-1}
                      checked={!!assign?.all || assign?.ids?.has(t.id)} />
                  </ListItemIcon>
                  <ListItemText primary={t.name}
                    secondary={[t.role, t.app_access_enabled ? null : 'sin acceso a la app']
                      .filter(Boolean).join(' · ') || null} />
                </ListItemButton>
              </ListItem>
            ))}
            {technicians.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No hay tecnicos dados de alta.
              </Typography>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssign(null)}>Cancelar</Button>
          <Button variant="contained" onClick={saveAssign} disabled={busy}>Asignar</Button>
        </DialogActions>
      </Dialog>

      {/* Visor: el mismo iframe en sandbox que ve el tecnico */}
      <Dialog open={!!viewer} onClose={() => setViewer(null)} fullWidth maxWidth="lg"
        PaperProps={{ sx: { height: '90vh' } }}>
        <DialogTitle sx={{ pb: 1 }}>{viewer?.title}</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {viewer && <HtmlDocViewer docId={viewer.id} title={viewer.title} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewer(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Quitar: siempre confirmado */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Quitar documento</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Quitar «{confirmDelete?.title}»? Desaparece del gestor y de la vista de
            todos los tecnicos que lo tuvieran asignado.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>No</Button>
          <Button color="error" variant="contained" onClick={doDelete} disabled={busy}>
            Si, quitar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
